import { FormikProps, useFormikContext } from "formik";

export type SetFieldValue = (
    value: any,
    field1: string,
    field2?: string,
    field3?: string,
    shouldValidate?: boolean
) => void;
export type SetFieldTouched = (
    field1: string,
    field2?: string,
    field3?: string
) => void;
type TouchErrors = () => Promise<void>;

export type RelodeFormikBaseProps<T> = {
    setFieldTouched: SetFieldTouched;
    setFieldValue: SetFieldValue;
    touchErrors: TouchErrors;
    formikProps: Omit<
        FormikProps<T>,
        "values" | "setFieldTouched" | "setFieldValue"
    >;
};

export const useRelodeFormik = <T extends object>(): {
    values: T;
    formik__infra: FormikProps<T>;
} & RelodeFormikBaseProps<T> => {
    const formik = useFormikContext<T>();

    const {
        values,
        setFieldValue: removed1,
        setFieldTouched: removed2,
        ..._formik
    } = formik;

    const formikProps = _formik as Omit<
        FormikProps<T>,
        "values" | "setFieldValue" | "setFieldTouched"
    >;

    const setFieldValue: SetFieldValue = async (
        value: any,
        field1: string,
        field2?: string,
        field3?: string,
        shouldValidate?: boolean,
    ) => {
        const propString = [field1, field2, field3]
            .filter(p => p !== undefined)
            .join(".");
        await formik.setFieldValue(propString as any, value);
        await formik.setFieldTouched(propString as any, true, !!shouldValidate);
    };

    const setFieldTouched: SetFieldTouched = (
        field1: string,
        field2?: string,
        field3?: string
    ) => {
        const propString = [field1, field2, field3]
            .filter(p => p !== undefined)
            .join(".");
        formik.setFieldTouched(propString as any);
    };

    const touchErrors: TouchErrors = async () => {
        const errs = await formik.validateForm();
        const toBeTouched = convertErrorsToTouched(errs);
        formik.setTouched(toBeTouched);
    };

    return {
        setFieldValue,
        setFieldTouched,
        touchErrors,
        formikProps,
        values,
        formik__infra: formik,
    };
};

const convertErrorsToTouched = <T extends object = any>(obj: T) => {
    const result = {};
    const keys = Object.keys(obj);

    keys.forEach(k => {
        if (obj[k].toString() === "[object Object]") {
            result[k] = convertErrorsToTouched(obj[k]);
        } else {
            result[k] = true;
        }
    });
    return result;
};
